import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleChevronLeft } from "@fortawesome/free-solid-svg-icons";
import WhiteMainBackground from "../../../shared_components/WhiteMainBackground/WhiteMainBackground";
import Spinner from "../../../shared_components/Spinner/Spinner";
import StyledButton from "../../../shared_components/StyledButton/StyledButton";
import { downloadAnimationToDisk } from "../actions/animationFileActions";
import { AnimationErrorMessages, getUserFacingMessage } from "../errors/animationErrors";
import AnimationService from "../../../services/AnimationService";
import UserData from "../../../services/UserData";
import styles from "./AiAnimationView.module.css";

function AiAnimationView() {
  const { animationId } = useParams();
  const navigate = useNavigate();
  const userData = useContext(UserData);

  const [animation, setAnimation] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [urlCopied, setUrlCopied] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  useEffect(() => {
    if (!animationId || !userData) return;

    setError(null);
    setLoading(true);

    AnimationService.viewPreview(animationId, userData.jwtToken, userData)
      .then((data) => {
        setAnimation(data);
        return AnimationService.streamVideo(
          animationId,
          userData.jwtToken,
          userData
        );
      })
      .then((streamResponse) => streamResponse.blob())
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        setVideoUrl(blobUrl);
      })
      .catch((fetchError) => {
        const detail = getUserFacingMessage(fetchError, "Unknown error");
        setError(AnimationErrorMessages.LOAD_DETAIL(detail));
      })
      .finally(() => {
        setLoading(false);
      });

    return () => {
      setVideoUrl((prev) => {
        if (prev && prev.startsWith("blob:")) {
          URL.revokeObjectURL(prev);
        }
        return null;
      });
    };
  }, [animationId, userData]);

  const handleDownload = async () => {
    try {
      const assetName = animation?.assetName;
      const filename = assetName
        ? `${assetName}.mp4`
        : animation?.filename || "animation.mp4";
      await downloadAnimationToDisk(
        animationId,
        userData.jwtToken,
        userData,
        filename
      );
    } catch (downloadError) {
      setError(
        getUserFacingMessage(
          downloadError,
          AnimationErrorMessages.DOWNLOAD
        )
      );
    }
  };

  const handleCopyPath = async () => {
    try {
      await navigator.clipboard.writeText(
        `${window.location.origin}/animations/${animationId}`
      );
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (copyError) {
      setError("Failed to copy animation URL.");
    }
  };

  const handleCopyPrompt = async () => {
    const prompt = animation?.revisedPrompt || animation?.promptUsed;
    if (!prompt) return;
    try {
      await navigator.clipboard.writeText(prompt);
      setPromptCopied(true);
      setTimeout(() => setPromptCopied(false), 2000);
    } catch (copyError) {
      setError("Failed to copy prompt.");
    }
  };

  if (loading) {
    return (
      <WhiteMainBackground>
        <Spinner />
      </WhiteMainBackground>
    );
  }

  if (error) {
    return (
      <WhiteMainBackground>
        <div className={styles.heading}>
          <FontAwesomeIcon
            className={styles.back_button}
            icon={faCircleChevronLeft}
            onClick={() => navigate(-1)}
            tabIndex={0}
            aria-label="Go back"
          />
          <span>Animation Detail</span>
        </div>
        <p>{error}</p>
      </WhiteMainBackground>
    );
  }

  if (!animation) {
    return (
      <WhiteMainBackground>
        <p>Animation not found</p>
      </WhiteMainBackground>
    );
  }

  return (
    <WhiteMainBackground>
      <div className={styles.container}>
        <div className={styles.heading}>
          <FontAwesomeIcon
            className={styles.back_button}
            icon={faCircleChevronLeft}
            onClick={() => navigate(-1)}
            tabIndex={0}
            aria-label="Go back"
          />
          <span>Generated Animation</span>
        </div>

        <div className={styles.panel}>
          <div className={styles.playerCard}>
            <div className={styles.playerFrame}>
              {videoUrl ? (
                <video
                  className={styles.video}
                  controls
                  src={videoUrl}
                />
              ) : (
                <div className={styles.previewPlaceholder}>
                  {animation.thumbnail_base64 ? (
                    <img
                      src={`data:image/jpeg;base64,${animation.thumbnail_base64}`}
                      alt={animation.assetName || "Animation thumbnail"}
                    />
                  ) : (
                    <p>Video preview not available</p>
                  )}
                </div>
              )}
            </div>

            <div className={styles.buttonRow}>
              <StyledButton className={styles.button} clickEvent={handleDownload}>
                Download
              </StyledButton>
              <StyledButton className={styles.button} clickEvent={handleCopyPath}>
                {urlCopied ? "Copied!" : "Copy URL"}
              </StyledButton>
              <StyledButton className={styles.button} clickEvent={handleCopyPrompt}>
                {promptCopied ? "Copied!" : "Copy Prompt"}
              </StyledButton>
            </div>
          </div>

          <div className={styles.detailsCard}>
            <h3 className={styles.detailsTitle}>Animation Details</h3>
            <div className={styles.metaRow}>
              <strong>Asset Name:</strong> {animation?.assetName || "N/A"}
            </div>
            <div className={styles.metaRow}>
              <strong>Status:</strong> {animation?.status || "N/A"}
            </div>
            <div className={styles.metaRow}>
              <strong>Created By:</strong> {animation?.createdBy || "N/A"}
            </div>
            <div className={styles.metaRow}>
              <strong>Date Created:</strong> {animation?.dateCreated || "N/A"}
            </div>
            <div className={styles.metaRow}>
              <strong>Filename:</strong> {animation?.filename || "N/A"}
            </div>
            <div className={styles.metaRow}>
              <strong>Prompt Used:</strong>
              <div className={styles.promptBlock}>{animation?.promptUsed || "N/A"}</div>
            </div>
          </div>
        </div>
      </div>
    </WhiteMainBackground>
  );
}

export default AiAnimationView;
